import { normalizeTemplateDomain, normalizeTemplateTheme } from './TemplateTheme.jsx';
import { resolveTemplateSectionsForReader } from './themeLinking.js';
import {
  EDUCATION_EXAM_THEME_DEFAULTS,
  EDUCATION_TEMPLATE_STYLES,
  getEducationTemplateStyle,
  isEducationExamTemplate
} from './EducationTemplateVariant.js';

export const ACTIVE_TEMPLATE_STORAGE_KEY = 'activeTemplate';
export const ACTIVE_TEMPLATE_SESSION_KEY = 'activeTemplate';
export const ACTIVE_TEMPLATE_API_STATE_PREFIX = 'activeTemplateApiState';
export const ACTIVE_TEMPLATE_API_READY_KEY = `${ACTIVE_TEMPLATE_API_STATE_PREFIX}:ready`;

const ACTIVE_TEMPLATE_PAGE_LOAD_TS = Date.now();

const flexOfficeImages = {
  HERO: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&q=85&fit=crop&auto=format',
  ABOUT: 'https://cdn.rareblocks.xyz/collection/celebration/images/content/3/girl-working-on-laptop.jpg',
  FEATURE_FLEXIBLE: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=75&fit=crop&auto=format',
  FEATURE_PREMIUM: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=600&q=75&fit=crop&auto=format',
  FEATURE_WIFI: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=75&fit=crop&auto=format',
  FEATURE_MEETING: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=600&q=75&fit=crop&auto=format',
  SERVICE_ROOM: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=900&q=80&fit=crop&auto=format',
  SERVICE_DESK: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=900&q=80&fit=crop&auto=format',
  SERVICE_LOUNGE: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=900&q=80&fit=crop&auto=format'
};

const clone = (value) => JSON.parse(JSON.stringify(value));
const TEMPLATE_PAYLOAD_KEYS = ['data', 'payload', 'result', 'template', 'uiTemplate'];

const safeParseTemplateJson = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const isTemplatePayload = (value) => (
  value
  && typeof value === 'object'
  && !Array.isArray(value)
  && (
    Array.isArray(value.pages)
    || Boolean(value.templateKey)
    || Boolean(value.baseTemplateKey)
    || Boolean(value.attributes?.sourceDomain)
    || Boolean(value.domain?.type)
  )
);

export const extractTemplatePayload = (payload) => {
  const value = safeParseTemplateJson(payload);
  if (!value) return null;

  if (Array.isArray(value)) {
    const templates = value.map(extractTemplatePayload).filter(Boolean);
    return templates.find((template) => template.systemDefault === false || template.company?.primaryId || template.companyId)
      || templates[0]
      || null;
  }

  if (isTemplatePayload(value)) {
    return value;
  }

  if (typeof value !== 'object') return null;
  if (String(value.status || '').toLowerCase() === 'failed') return null;

  for (const key of TEMPLATE_PAYLOAD_KEYS) {
    if (value[key] === undefined || value[key] === value) continue;
    const template = extractTemplatePayload(value[key]);
    if (template) return template;
  }

  return null;
};

const asset = (displayLink = '') => ({ storedLink: '', displayLink });
const action = (label, url, variant = 'primary') => ({ label, url, variant, visible: true });
const block = (title, description = '', icon = '', extra = {}) => ({ title, description, icon, visible: true, ...extra });
const legacySection = (sectionId, order, componentKey, previewKey = componentKey, overrides = {}) => ({
  sectionId,
  type: 'legacyComponent',
  order,
  visible: true,
  layout: { align: 'left', width: 'full' },
  style: { backgroundColor: '#ffffff', color: '#1f2937', padding: '0' },
  content: { componentKey, previewKey, overrides: {}, ...(overrides.content || {}) },
  bindings: { componentKey, previewKey, useLiveComponent: true, ...(overrides.bindings || {}) },
  customHtml: '',
  customCss: '',
  ...Object.fromEntries(Object.entries(overrides).filter(([key]) => !['content', 'bindings'].includes(key)))
});

const flexStyle = (overrides = {}) => ({
  backgroundColor: '#ffffff',
  color: '#1e293b',
  accentColor: '#2563eb',
  secondaryColor: '#0f172a',
  padding: '80px 24px',
  ...overrides
});

const flexSection = (sectionId, type, order, layout, style, content, bindings = {}) => ({
  sectionId,
  type,
  order,
  visible: true,
  layout,
  style,
  content,
  bindings,
  customHtml: '',
  customCss: ''
});

const flexOfficeTheme = {
  primary: '#2563eb',
  secondary: '#0f172a',
  accent: '#1d4ed8',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#1e293b',
  mutedColor: '#64748b',
  borderColor: '#e2e8f0',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1280px',
  buttonRadius: '12px',
  cardRadius: '20px',
  cardShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
  imageRadius: '20px',
  headerHeight: '64px',
  footerBackground: '#0f172a'
};

const flexOfficeHeader = {
  visible: true,
  sticky: true,
  height: '64px',
  backgroundColor: '#ffffff',
  borderColor: '#f1f5f9',
  shadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
  logoUrl: '',
  logoContent: asset(''),
  logoAlt: 'FlexOffice',
  brandName: 'FlexOffice',
  ctaLabel: 'Inquire Now',
  ctaUrl: '/contact',
  signInLabel: 'Login',
  signUpLabel: 'Sign Up',
  nav: [
    { label: 'Home', route: '/', icon: 'Home', visible: true },
    { label: 'Features', route: '/our-features', icon: 'Star', visible: true },
    { label: 'Inquire Now', route: '/contact', icon: 'Help', highlight: true, visible: true }
  ]
};

const flexOfficeFooter = {
  visible: true,
  backgroundColor: '#0f172a',
  textColor: '#cbd5e1',
  headingColor: '#ffffff',
  linkColor: '#93c5fd',
  socialTitle: 'Connect With Us',
  navigationTitle: 'Navigation',
  legalTitle: 'Legal Documents',
  contactTitle: 'Contact Information',
  copyrightText: 'All Rights Reserved.',
  poweredByText: 'Powered by',
  poweredByLabel: 'www.krumago.com',
  poweredByUrl: 'https://www.krumago.com',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Contact Us', route: '/contact', visible: true },
    { label: 'About Us', route: '/our-features', visible: true }
  ],
  legalLinks: [
    { label: 'Privacy Policy', route: '/privacy', visible: true },
    { label: 'Terms of Service', route: '/terms', visible: true },
    { label: 'Cookie Policy', route: '/cookies', visible: true },
    { label: 'Refund Policy', route: '/refund', visible: true }
  ],
  socialLinks: [
    { label: 'Facebook', route: 'https://www.facebook.com/', visible: true },
    { label: 'Twitter', route: 'https://twitter.com/', visible: true },
    { label: 'Instagram', route: 'https://www.instagram.com/', visible: true },
    { label: 'LinkedIn', route: 'https://www.linkedin.com/', visible: true }
  ],
  contact: {
    address: '123 Workspace Ave, Suite 400, New York, NY 10001',
    phone: '+1 (555) 248-1010',
    email: 'hello@flexoffice.com'
  }
};

const flexOfficeServicesSection = (order = 1) => flexSection(
  'default-flex-office-services',
  'flexOfficeServicesPage',
  order,
  { align: 'left', width: 'contained', columns: '3', stackOnMobile: true },
  flexStyle({ backgroundColor: '#f8fafc', padding: '104px 24px' }),
  {
    title: 'All Services',
    subtitle: 'Browse conference rooms, workstations, private offices, and collaboration spaces.',
    searchPlaceholder: 'Search products...',
    categoryLabel: 'All Categories',
    blocks: [
      block('Executive Conference Room', 'Boardroom setup with display, whiteboard, and premium seating.', 'Meeting', { imageUrl: flexOfficeImages.SERVICE_ROOM, imageContent: asset(flexOfficeImages.SERVICE_ROOM), price: '$45', period: 'per hour', badge: 'Meeting Room', buttonLabel: 'View Details', url: '/selected-product-details' }),
      block('Dedicated Workstation', 'Quiet desk with fast internet, power, and ergonomic setup.', 'Desk', { imageUrl: flexOfficeImages.SERVICE_DESK, imageContent: asset(flexOfficeImages.SERVICE_DESK), price: '$18', period: 'per hour', badge: 'Workspace', buttonLabel: 'View Details', url: '/selected-product-details' }),
      block('Team Collaboration Lounge', 'Open lounge for workshops, brainstorming, and client sessions.', 'Users', { imageUrl: flexOfficeImages.SERVICE_LOUNGE, imageContent: asset(flexOfficeImages.SERVICE_LOUNGE), price: '$75', period: 'per hour', badge: 'Team Space', buttonLabel: 'View Details', url: '/selected-product-details' })
    ]
  }
);

const flexOfficeServiceDetailSection = (order = 1) => flexSection(
  'default-flex-office-service-detail',
  'flexOfficeServiceDetail',
  order,
  { align: 'left', width: 'contained', columns: '2', stackOnMobile: true },
  flexStyle({ backgroundColor: '#ffffff', padding: '96px 24px' }),
  {
    eyebrow: 'Service Detail',
    title: 'Executive Conference Room',
    subtitle: 'A polished room for client meetings, board sessions, interviews, and collaborative workshops.',
    imageUrl: flexOfficeImages.SERVICE_ROOM,
    imageContent: asset(flexOfficeImages.SERVICE_ROOM),
    priceLabel: '$45',
    periodLabel: 'per hour',
    primaryActionLabel: 'Continue Booking',
    primaryActionUrl: '/review-schedule',
    blocks: [
      block('Capacity', 'Seats up to 12 people with presentation setup.', 'Users'),
      block('Amenities', 'Display, whiteboard, video call support, and high speed Wi-Fi.', 'Sparkles'),
      block('Flexible terms', 'Book hourly, daily, weekly, or recurring blocks.', 'Calendar')
    ]
  }
);

const flexOfficeBookingSearchSection = (order = 1) => flexSection(
  'default-flex-office-booking-search',
  'flexOfficeBookingSearch',
  order,
  { align: 'left', width: 'contained', columns: '4', stackOnMobile: true },
  flexStyle({ backgroundColor: '#eff6ff', padding: '80px 24px' }),
  {
    eyebrow: 'Book Space',
    title: 'Find the right workspace',
    subtitle: 'Search by location, date, duration, and space type. Live availability stays connected to the booking flow.',
    locationLabel: 'Location',
    dateLabel: 'Date',
    durationLabel: 'Duration',
    typeLabel: 'Space Type',
    primaryActionLabel: 'Search Spaces',
    primaryActionUrl: '/flex-home',
    blocks: [
      block('Downtown', 'Main location', 'MapPin'),
      block('Today', 'Available slots', 'Calendar'),
      block('2 hours', 'Flexible duration', 'Clock'),
      block('Meeting room', 'Workspace type', 'Building')
    ]
  }
);

const flexOfficeInvoiceSection = (order = 1) => flexSection(
  'default-flex-office-invoice',
  'flexOfficeInvoice',
  order,
  { align: 'left', width: 'contained', columns: '2', stackOnMobile: true },
  flexStyle({ backgroundColor: '#ffffff', padding: '96px 24px' }),
  {
    title: 'Invoices',
    subtitle: 'Track invoice status, payment references, and receipts for workspace bookings.',
    totalLabel: 'Total Due',
    statusLabel: 'Payment Status',
    primaryActionLabel: 'View Invoice',
    primaryActionUrl: '/my-invoice',
    blocks: [
      block('INV-1008', 'Executive Conference Room', 'Receipt', { badge: 'Due', price: '$90' }),
      block('INV-1007', 'Dedicated Workstation', 'Receipt', { badge: 'Paid', price: '$72' })
    ]
  }
);

const flexOfficeDefaultTemplate = {
  primaryId: -2001,
  templateKey: 'FLEX_OFFICE_DEFAULT',
  name: 'Flex Office Default',
  description: 'Default Flex Office landing template',
  lifecycleStatus: 'PUBLISHED',
  version: 1,
  systemDefault: true,
  baseTemplateKey: 'FlexOfficeMain',
  company: null,
  domain: { type: 'FLEX_OFFICE' },
  attributes: {
    schemaVersion: 1,
    sourceDomain: 'FLEX_OFFICE',
    sourceTemplate: 'FlexOfficeMain',
    sourceComponent: 'FlexOfficeTemplateRenderer',
    runtime: { mode: 'structured', renderer: 'flexOffice', renderMode: 'legacy-parity' },
    theme: flexOfficeTheme,
    header: { ...flexOfficeHeader, mode: 'legacy' },
    footer: { ...flexOfficeFooter, mode: 'legacy' },
    pageMeta: {}
  },
  pages: [
    { pageKey: 'home', route: '/', title: 'Flex Office', enabled: true, sections: [legacySection('flex-office-main', 1, 'FlexOfficeMain', 'flexOfficeHome')] },
    { pageKey: 'bookingHome', route: '/flex-home', title: 'Book Space', enabled: true, sections: [flexOfficeBookingSearchSection(1)] },
    { pageKey: 'bookSpace', route: '/book-space', title: 'Book Space', enabled: true, sections: [legacySection('flex-office-book-space', 1, 'SelectSlot', 'flexOfficeBookSpace')] },
    { pageKey: 'spaceTypes', route: '/type-of-spaces', title: 'Space Types', enabled: true, sections: [legacySection('flex-office-space-types', 1, 'ViewAllCategories', 'flexOfficeSpaceTypes')] },
    { pageKey: 'exploreLocations', route: '/explore-locations', title: 'Explore Locations', enabled: true, sections: [legacySection('flex-office-explore-locations', 1, 'ExploreLocation', 'flexOfficeExploreLocations')] },
    { pageKey: 'bookSchedule', route: '/book-schedule', title: 'Book Schedule', enabled: true, sections: [{
      ...flexOfficeBookingSearchSection(1),
      sectionId: 'default-flex-office-book-schedule',
      content: {
        ...flexOfficeBookingSearchSection(1).content,
        eyebrow: 'Book Schedule',
        title: 'Choose a time that works',
        subtitle: 'Review available dates, durations, and workspace types before confirming your reservation.',
        primaryActionLabel: 'Review Schedule',
        primaryActionUrl: '/review-schedule'
      }
    }] },
    { pageKey: 'choice', route: '/choice', title: 'Choose Space', enabled: true, sections: [{
      ...flexOfficeServicesSection(1),
      sectionId: 'default-flex-office-choice',
      content: {
        ...flexOfficeServicesSection(1).content,
        title: 'Choose Your Space',
        subtitle: 'Compare meeting rooms, workstations, and collaboration lounges before continuing to booking.'
      }
    }] },
    { pageKey: 'viewService', route: '/view-service', title: 'View Service', enabled: true, sections: [legacySection('flex-office-view-service', 1, 'SelectSchedule', 'flexOfficeViewService')] },
    { pageKey: 'services', route: '/our-services', title: 'Services', enabled: true, sections: [legacySection('flex-office-services', 1, 'ViewAllServices', 'flexOfficeServices')] },
    { pageKey: 'serviceDetail', route: '/selected-product-details', title: 'Service Detail', enabled: true, sections: [flexOfficeServiceDetailSection(1)] },
    { pageKey: 'features', route: '/our-features', title: 'Features', enabled: true, sections: [
      legacySection('flex-office-features', 1, 'WhyChooseUsSection', 'flexOfficeFeatures'),
      legacySection('flex-office-features-timeline', 2, 'FlexOfficeTimeline', 'flexOfficeFeaturesTimeline'),
      legacySection('flex-office-features-working-hours', 3, 'WorkingHoursSection', 'flexOfficeFeaturesWorkingHours'),
      legacySection('flex-office-features-cta', 4, 'CallToActionSection', 'flexOfficeFeaturesCta'),
    ] },
    { pageKey: 'marketingFeatures', route: '/features', title: 'Features', enabled: true, sections: [legacySection('flex-office-marketing-features', 1, 'FlexFeatures', 'flexOfficeMarketingFeatures')] },
    { pageKey: 'confirmBook', route: '/confirm-book', title: 'Confirm Booking', enabled: true, sections: [legacySection('flex-office-confirm-book', 1, 'ConfirmBook', 'flexOfficeConfirmBook')] },
    { pageKey: 'reviewSchedule', route: '/review-schedule', title: 'Review Schedule', enabled: true, sections: [legacySection('flex-office-review-schedule', 1, 'ReviewSchedule', 'flexOfficeReviewSchedule')] },
    { pageKey: 'bookings', route: '/my-bookings', title: 'My Bookings', enabled: true, sections: [legacySection('flex-office-bookings', 1, 'ViewMyOrder', 'flexOfficeBookings')] },
    { pageKey: 'orders', route: '/my-orders', title: 'My Orders', enabled: true, sections: [legacySection('flex-office-orders', 1, 'ViewMyOrder', 'flexOfficeOrders')] },
    { pageKey: 'customerOrders', route: '/customer-orders', title: 'Customer Orders', enabled: true, sections: [legacySection('flex-office-customer-orders', 1, 'ViewOrderDetails', 'flexOfficeCustomerOrders')] },
    { pageKey: 'userCustomerOrders', route: '/user-profile/customer-orders', title: 'Customer Orders', enabled: true, sections: [legacySection('flex-office-user-customer-orders', 1, 'ViewMyOrder', 'flexOfficeUserCustomerOrders')] },
    { pageKey: 'invoice', route: '/my-invoice', title: 'Invoices', enabled: true, sections: [flexOfficeInvoiceSection(1)] },
    { pageKey: 'invoiceDetail', route: '/my-invoice/:id', title: 'Invoice Detail', enabled: true, sections: [legacySection('flex-office-invoice-detail', 1, 'ViewMyInvoice', 'flexOfficeInvoiceDetail')] },
    { pageKey: 'aboutFlexOffice', route: '/about-flexoffice', title: 'About FlexOffice', enabled: true, sections: [legacySection('flex-office-about-page', 1, 'FlexOfficeAbout', 'flexOfficeAboutPage')] },
    { pageKey: 'contact', route: '/contact', title: 'Contact', enabled: true, sections: [legacySection('flex-office-contact', 1, 'KrumagoContact', 'sharedContact')] },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', enabled: true, sections: [legacySection('flex-office-sign-in', 1, 'Login', 'authSignIn')] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', enabled: true, sections: [legacySection('flex-office-sign-up', 1, 'Register', 'flexOfficeSignUp')] }
  ],
  dynamicAttributes: [{ name: 'templateKey', value: 'FLEX_OFFICE_DEFAULT' }]
};

const domainStyle = (overrides = {}) => ({
  backgroundColor: '#ffffff',
  color: '#1f2937',
  padding: '80px 24px',
  ...overrides
});

const domainSection = (sectionId, type, order, layout, style, content, bindings = {}) => ({
  sectionId,
  type,
  order,
  visible: true,
  layout,
  style,
  content,
  bindings,
  customHtml: '',
  customCss: ''
});

const educationImages = {
  HERO: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1920&q=85&fit=crop&auto=format',
  COURSE: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900&q=80&fit=crop&auto=format',
  CLOUD: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=900&q=80&fit=crop&auto=format',
  ABOUT: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&q=80&fit=crop&auto=format',
  GUIDANCE: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=900&q=80&fit=crop&auto=format',
  CONTACT: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Education+Domain+Assets/customer-care.jpg',
  E_LEARNING: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/e-learning.png',
  E_LEARNING_ALT: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/elearning.png',
  CURRICULUM: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/curriculum.png',
  PERSONAL_TRAINER: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/personalTrainer.png',
  CLASSROOM: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/classRoom.png',
  BUILDING: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/building.png',
  INCLUSIVITY: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/inclusivity.png',
  JOIN_US_ABOUT: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/joinUSAbout.png'
};

const educationTheme = {
  primary: '#3B9394',
  secondary: '#746EAC',
  accent: '#2563eb',
  background: '#ffffff',
  surface: '#ffffff',
  color: '#1f2937',
  mutedColor: '#4b5563',
  borderColor: '#e5e7eb',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1280px',
  buttonRadius: '8px',
  cardRadius: '16px',
  cardShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
  footerBackground: '#f3f4f6'
};

const educationHeader = {
  visible: true,
  sticky: true,
  height: '70px',
  backgroundColor: '#ffffff',
  borderColor: '#f1f5f9',
  shadow: '0 2px 10px rgba(0,0,0,0.08)',
  logoAlt: 'AVPS Academy',
  searchPlaceholder: 'Search courses, categories...',
  searchButtonLabel: 'Search',
  cartLabel: 'Cart',
  signInLabel: 'Sign In',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Courses', route: '/course', visible: true },
    { label: 'About', route: '/about', visible: true },
    { label: 'Contact', route: '/contact', visible: true }
  ]
};

const educationFooter = {
  visible: true,
  backgroundColor: '#f3f4f6',
  textColor: '#374151',
  headingColor: '#1f2937',
  linkColor: '#3B9394',
  quickLinksTitle: 'Quick Links',
  socialTitle: 'Follow Us',
  contactTitle: 'Contact',
  legalTitle: 'Legal',
  copyrightName: 'AVPS Academy',
  poweredByText: 'Powered by',
  poweredByLabel: 'www.krumago.com',
  poweredByUrl: 'https://www.krumago.com',
  quickLinks: [
    { label: 'Courses', route: '/course', visible: true },
    { label: 'About', route: '/about', visible: true },
    { label: 'Contact', route: '/contact', visible: true }
  ],
  socialLinks: [
    { label: 'Facebook', route: 'https://www.facebook.com/', visible: true },
    { label: 'LinkedIn', route: 'https://www.linkedin.com/', visible: true }
  ],
  legalLinks: [
    { label: 'Privacy Policy', route: '/privacy', visible: true },
    { label: 'Terms', route: '/terms', visible: true }
  ],
  contact: { phone: '+1 244-5333-7783', email: 'info@avpstech.com' }
};

const educationProductsPageSection = (order = 1) => domainSection(
  'education-products',
  'educationProductsPage',
  order,
  { align: 'left', width: 'contained' },
  domainStyle({ backgroundColor: '#f8fafc', padding: '56px 24px', maxWidth: '1536px' }),
  { title: 'Courses', subtitle: 'Discover expert-led courses that match your career goals.' }
);

const educationCourseDetailPageSection = (order = 1) => domainSection(
  'education-course-detail',
  'educationCourseDetail',
  order,
  { align: 'left', width: 'contained' },
  domainStyle({ backgroundColor: '#ffffff', padding: '56px 24px', maxWidth: '1536px' }),
  {
    title: 'Course Detail',
    subtitle: 'Review curriculum, schedule, instructor information, and enrollment options.',
    imageUrl: educationImages.COURSE
  }
);

const educationAboutPageSection = (order = 1) => domainSection(
  'education-about',
  'educationAbout',
  order,
  { align: 'left', width: 'contained', columns: '2', stackOnMobile: true },
  domainStyle({ backgroundColor: '#ffffff', padding: '88px 24px' }),
  {
    eyebrow: 'About Us',
    title: 'AVPS Academy',
    subtitle: 'At the AVPS Academy, we are committed to providing unparalleled educational experiences for our students. Our esteemed faculty members are experts in their respective fields and are dedicated to helping students achieve their academic and personal goals. With state-of-the-art facilities and a comprehensive curriculum, we offer a dynamic learning environment that nurtures intellectual curiosity, critical thinking skills, and personal growth.',
    heroImageUrl: educationImages.E_LEARNING,
    heroImageContent: asset(educationImages.E_LEARNING),
    aboutSectionTitle: 'Why Choose the AVPS Academy?',
    showWhyChoose: true,
    showSignupBanner: true,
    signupImageUrl: educationImages.JOIN_US_ABOUT,
    signupImageContent: asset(educationImages.JOIN_US_ABOUT),
    signupButtonLabel: 'Sign Up',
    signupButtonUrl: '/sign-in',
    blocks: [
      block('Cutting-Edge Curriculum', 'Immerse your child in an advanced curriculum designed to foster innovation and adaptability in a rapidly changing world.', 'Curriculum', { imageUrl: educationImages.CURRICULUM }),
      block('Expert Faculty', 'Learn from a team of passionate, experienced educators dedicated to nurturing individual student potential.', 'Faculty', { imageUrl: educationImages.PERSONAL_TRAINER }),
      block('Small Class Sizes', 'Enjoy personalized learning experiences in small class settings that prioritize student engagement and interaction.', 'Class', { imageUrl: educationImages.CLASSROOM }),
      block('State-of-the-Art Facilities', 'Discover our cutting-edge learning environments, featuring top-tier technology and resources for modern education.', 'Facilities', { imageUrl: educationImages.BUILDING }),
      block('Inclusive and Diverse Community', 'Be part of a community that values and celebrates diversity, creating a safe and inclusive space for all students.', 'Community', { imageUrl: educationImages.INCLUSIVITY }),
      block('Parental Involvement', "Partner with us in your child's education, fostering a collaborative relationship that strengthens their academic journey.", 'Guidance', { imageUrl: educationImages.GUIDANCE }),
      block('Strong Values-Based Education', 'Shape character and leadership through our commitment to teaching strong ethical values and social responsibility.', 'Values', { imageUrl: educationImages.E_LEARNING_ALT })
    ]
  }
);

const educationContactPageSection = (order = 1) => domainSection(
  'education-contact',
  'educationContact',
  order,
  { align: 'left', width: 'contained', columns: '2', stackOnMobile: true },
  domainStyle({ backgroundColor: '#ffffff', padding: '96px 24px 64px' }),
  {
    eyebrow: 'Contact',
    title: 'Get in',
    titleAccent: 'touch',
    subtitle: "Have questions about admissions or courses? We're here to guide you.",
    imageUrl: educationImages.CONTACT,
    imageContent: asset(educationImages.CONTACT),
    imageHeading: "Let's Connect",
    imageSubtitle: 'Building relationships that matter',
    phoneTitle: 'Phone',
    phoneValue: '+1 244-5333-7783',
    emailTitle: 'Email',
    emailValue: 'info@avpstech.com',
    addressTitle: 'Address',
    addressValue: 'AVPS Academy',
    formTitle: 'Send us a Message',
    submitLabel: 'Send Message',
    fields: [
      { label: 'First Name', type: 'text', visible: true },
      { label: 'Last Name', type: 'text', visible: true },
      { label: 'Email Address', type: 'email', required: true, visible: true },
      { label: 'Phone Number', type: 'tel', visible: true },
      { label: 'Message', type: 'textarea', required: true, visible: true }
    ]
  }
);

const educationDefaultTemplate = {
  primaryId: -1001,
  templateKey: 'EDUCATION_DEFAULT',
  name: 'Education Default',
  description: 'Default Education storefront template',
  lifecycleStatus: 'PUBLISHED',
  version: 1,
  systemDefault: true,
  baseTemplateKey: 'EducationDomainTemplate',
  company: null,
  domain: { type: 'EDUCATION' },
  attributes: {
    schemaVersion: 3,
    sourceDomain: 'EDUCATION',
    sourceTemplate: 'EducationDomainTemplate',
    sourceComponent: 'EducationDomainTemplate',
    runtime: { mode: 'structured', renderer: 'education', renderMode: 'legacy-parity' },
    theme: educationTheme,
    header: { ...educationHeader, mode: 'legacy' },
    footer: { ...educationFooter, mode: 'legacy' },
    pageMeta: {}
  },
  pages: [
    { pageKey: 'home', route: '/', title: 'Education Default', enabled: true, sections: [legacySection('education-home', 1, 'EducationDomainTemplate', 'educationHome')] },
    { pageKey: 'products', route: '/course', title: 'Courses', enabled: true, sections: [educationProductsPageSection(1)] },
    { pageKey: 'courseDetail', route: '/course-detail', title: 'Course Detail', enabled: true, sections: [educationCourseDetailPageSection(1)] },
    { pageKey: 'cart', route: '/cart', title: 'Cart', enabled: true, sections: [legacySection('education-cart', 1, 'Cart', 'educationCart')] },
    { pageKey: 'summary', route: '/order-history', title: 'Summary', enabled: true, sections: [legacySection('education-summary', 1, 'OrderHistory', 'educationSummary')] },
    { pageKey: 'about', route: '/about', title: 'About', enabled: true, sections: [educationAboutPageSection(1)] },
    { pageKey: 'aboutAcademy', route: '/about-academy', title: 'About Academy', enabled: true, sections: [{ ...educationAboutPageSection(1), sectionId: 'education-about-academy' }] },
    { pageKey: 'contact', route: '/contact', title: 'Contact', enabled: true, sections: [educationContactPageSection(1)] },
    { pageKey: 'bridgingKnowledgeGap', route: '/BridgingTheKnowledgeGap', title: 'Bridging the Knowledge Gap', enabled: true, sections: [legacySection('education-info-bridging', 1, 'BridgingTheKnowledgeGap', 'educationBridgingKnowledgeGap')] },
    { pageKey: 'insightsEngagement', route: '/InsightsAndEngagement', title: 'Insights & Engagement', enabled: true, sections: [legacySection('education-info-insights', 1, 'InsightsAndEngagement', 'educationInsightsEngagement')] },
    { pageKey: 'industryExperience', route: '/BuildIndustryExperience', title: 'Build Industry Experience', enabled: true, sections: [legacySection('education-info-industry', 1, 'BuildIndustryExperience', 'educationIndustryExperience')] },
    { pageKey: 'groomingSkills', route: '/GroomingSkills', title: 'Technical & Soft Skills', enabled: true, sections: [legacySection('education-info-grooming', 1, 'GroomingTechnicalAndSoftSkills', 'educationGroomingSkills')] },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', enabled: true, sections: [legacySection('education-sign-in', 1, 'Login', 'authSignIn')] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', enabled: true, sections: [legacySection('education-sign-up', 1, 'Signup', 'authSignUp')] }
  ],
  dynamicAttributes: [{ name: 'templateKey', value: 'EDUCATION_DEFAULT' }]
};

const asEducationExamPage = (sourcePage) => ({
  ...clone(sourcePage),
  title: sourcePage.pageKey === 'home'
    ? 'Exam Portal'
    : sourcePage.pageKey === 'products'
      ? 'Exams'
      : sourcePage.pageKey === 'courseDetail'
        ? 'Exam Detail'
        : sourcePage.title,
  attributes: {
    ...(sourcePage.attributes || {}),
    educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM
  },
  sections: (sourcePage.sections || []).map((sourceSection) => ({
    ...sourceSection,
    style: {
      ...(sourceSection.style || {}),
      accentColor: sourceSection.style?.accentColor || EDUCATION_EXAM_THEME_DEFAULTS.accent,
      secondaryColor: sourceSection.style?.secondaryColor || EDUCATION_EXAM_THEME_DEFAULTS.primary
    },
    content: {
      ...(sourceSection.content || {}),
      educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM
    }
  }))
});

/**
 * Exam Portal home section. Kept in parity with the console builder's
 * `createEducationExamHomeSection` (admin-console-ui education template defaults) so the
 * builder preview and this reader render the same Exam landing page. The section's own
 * copy lives in EducationExamHomeSection.jsx; only overrides belong here.
 */
const educationExamHomeSection = (order = 1) => ({
  sectionId: 'education-exam-home-hero',
  type: 'educationExamHome',
  order,
  visible: true,
  layout: { align: 'left', width: 'full' },
  style: {
    backgroundColor: EDUCATION_EXAM_THEME_DEFAULTS.primary,
    color: '#ffffff',
    accentColor: EDUCATION_EXAM_THEME_DEFAULTS.accent,
    secondaryColor: EDUCATION_EXAM_THEME_DEFAULTS.secondary,
    padding: '0'
  },
  content: {
    titlePrefix: 'Exam',
    titleSuffix: 'Anywhere',
    titleAccent: 'Certify Faster',
    educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM
  },
  bindings: { sourceComponent: 'EducationExamTemplate' },
  customHtml: '',
  customCss: ''
});

/**
 * Exam Portal inner page sections. Each carries the shared design's own nav/footer, so all
 * four types are listed in selfChromedSections.js. Kept in parity with the console builder's
 * `createEducationExamPageSection` in educationTemplateDefaults.js.
 */
const educationExamInnerSection = (sectionId, type) => () => ({
  sectionId,
  type,
  order: 1,
  visible: true,
  layout: { align: 'left', width: 'full' },
  style: {
    backgroundColor: EDUCATION_EXAM_THEME_DEFAULTS.primary,
    color: '#ffffff',
    accentColor: EDUCATION_EXAM_THEME_DEFAULTS.accent,
    secondaryColor: EDUCATION_EXAM_THEME_DEFAULTS.secondary,
    padding: '0'
  },
  content: { educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM },
  bindings: { sourceComponent: 'EducationExamTemplate' },
  customHtml: '',
  customCss: ''
});

/**
 * Functional Exam pages keep their live component: the section stays a legacy wrapper for
 * `componentKey`, but its type is `educationExamShell` so the Exam frame (nav, page head,
 * footer) renders around the real cart / order-history / auth component.
 */
const educationExamShellSection = (sectionId, componentKey, previewKey, content) => () => ({
  ...legacySection(sectionId, 1, componentKey, previewKey),
  type: 'educationExamShell',
  style: {
    backgroundColor: EDUCATION_EXAM_THEME_DEFAULTS.primary,
    color: '#ffffff',
    accentColor: EDUCATION_EXAM_THEME_DEFAULTS.accent,
    secondaryColor: EDUCATION_EXAM_THEME_DEFAULTS.secondary,
    padding: '0'
  },
  content: {
    componentKey,
    previewKey,
    overrides: {},
    educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM,
    ...content
  }
});

const educationExamInfoSection = (sectionId, content) => () => ({
  ...educationExamInnerSection(sectionId, 'educationExamInfo')(),
  content: { educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM, ...content }
});

const educationExamPageSections = {
  home: educationExamHomeSection,
  products: educationExamInnerSection('education-exam-courses', 'educationExamCourses'),
  courseDetail: educationExamInnerSection('education-exam-course-detail', 'educationExamCourseDetail'),
  about: educationExamInnerSection('education-exam-about', 'educationExamAbout'),
  aboutAcademy: educationExamInnerSection('education-exam-about-academy', 'educationExamAbout'),
  contact: educationExamInnerSection('education-exam-contact', 'educationExamContact'),
  bridgingKnowledgeGap: educationExamInfoSection('education-exam-info-bridging', {
    crumb: 'Closing the skill gap',
    eyebrow: 'Before you attempt',
    title: 'Close the gap the blueprint exposes.',
    bodyTitle: 'Turn blueprint weaknesses into passes'
  }),
  insightsEngagement: educationExamInfoSection('education-exam-info-insights', {
    crumb: 'Insights & analytics',
    eyebrow: 'After your attempt',
    title: 'Read your result like a report, not a score.',
    bodyTitle: 'What your section analytics tell you'
  }),
  industryExperience: educationExamInfoSection('education-exam-info-industry', {
    crumb: 'Industry relevance',
    eyebrow: 'Why it counts',
    title: 'Assessments mapped to real engineering work.',
    bodyTitle: 'How questions reflect practice'
  }),
  groomingSkills: educationExamInfoSection('education-exam-info-grooming', {
    crumb: 'Technical & soft skills',
    eyebrow: 'Full picture',
    title: 'Certify judgement, not just recall.',
    bodyTitle: 'Beyond memorisation'
  }),
  cart: educationExamShellSection('education-exam-cart', 'Cart', 'educationCart', {
    crumb: 'Cart', eyebrow: 'Exam registration', title: 'Your exam cart'
  }),
  summary: educationExamShellSection('education-exam-summary', 'OrderHistory', 'educationSummary', {
    crumb: 'Order history', eyebrow: 'Your registrations', title: 'Registrations and receipts'
  }),
  signIn: educationExamShellSection('education-exam-sign-in', 'Login', 'authSignIn', {
    crumb: 'Sign in', eyebrow: 'Candidate access', title: 'Sign in to your exam account'
  }),
  signUp: educationExamShellSection('education-exam-sign-up', 'Signup', 'authSignUp', {
    crumb: 'Register', eyebrow: 'Candidate access', title: 'Create your candidate account'
  })
};

const educationExamTemplate = {
  ...clone(educationDefaultTemplate),
  primaryId: -1002,
  templateKey: 'EDUCATION_EXAM',
  name: 'Education Exam',
  description: 'Complete dark-teal Education and exam portal template',
  baseTemplateKey: 'EducationExamTemplate',
  attributes: {
    ...clone(educationDefaultTemplate.attributes),
    educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM,
    theme: {
      ...clone(educationDefaultTemplate.attributes.theme),
      ...EDUCATION_EXAM_THEME_DEFAULTS
    },
    // Chrome stays enabled here: the Exam home page hides it per-page because
    // `educationExamHome` embeds the design's own nav/footer (see TemplateRenderer's
    // selfChromedSectionTypes). The inner pages still need this header and footer.
    header: {
      ...clone(educationDefaultTemplate.attributes.header),
      searchPlaceholder: 'Search exams, categories...',
      nav: clone(educationDefaultTemplate.attributes.header.nav).map((item) => (
        item.route === '/course' ? { ...item, label: 'Exams' } : item
      )),
      borderColor: '#d9e7eb',
      shadow: '0 8px 24px rgba(8, 64, 88, 0.08)'
    },
    footer: {
      ...clone(educationDefaultTemplate.attributes.footer),
      quickLinks: clone(educationDefaultTemplate.attributes.footer.quickLinks).map((item) => (
        item.route === '/course' ? { ...item, label: 'Exams' } : item
      )),
      backgroundColor: '#112f2d',
      textColor: '#dbeafe',
      linkColor: '#ffffff',
      headingColor: '#d8ff91',
      borderColor: '#0d526d'
    }
  },
  pages: educationDefaultTemplate.pages.map(asEducationExamPage).map((page) => {
    const examSection = educationExamPageSections[page.pageKey];
    if (!examSection) return page;
    return {
      ...page,
      title: page.pageKey === 'home' ? 'Exam Portal' : page.title,
      sections: [examSection()]
    };
  }),
  dynamicAttributes: [{ name: 'templateKey', value: 'EDUCATION_EXAM' }]
};

export const getEducationExamTemplateDefaults = () => clone(educationExamTemplate);

const isDefaultEducationLegacyHomePage = (page) => {
  if (!page || page.pageKey !== 'home' || !Array.isArray(page.sections) || page.sections.length !== 1) return false;
  const section = page.sections[0] || {};
  const componentKey = section.content?.componentKey || section.bindings?.componentKey;
  const previewKey = section.content?.previewKey || section.bindings?.previewKey;
  return section.type === 'legacyComponent'
    && componentKey === 'EducationDomainTemplate'
    && (previewKey === 'educationHome' || previewKey === 'EducationDomainTemplate');
};

const retailTheme = {
  primary: '#0891b2',
  secondary: '#232f3e',
  accent: '#B88E2F',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#111827',
  mutedColor: '#6b7280',
  borderColor: '#e5e7eb',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1280px',
  buttonRadius: '999px',
  cardRadius: '14px',
  cardShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
  footerBackground: '#232f3e'
};

const retailHeader = {
  visible: true,
  sticky: true,
  height: '72px',
  backgroundColor: '#ffffff',
  brandName: 'Retail Store',
  searchPlaceholder: 'Search products, brands, categories',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Products', route: '/product', visible: true },
    { label: 'About', route: '/about', visible: true },
    { label: 'Contact', route: '/contact', visible: true }
  ]
};

const retailFooter = {
  visible: true,
  backgroundColor: '#232f3e',
  textColor: '#ffffff',
  headingColor: '#ffffff',
  linkColor: '#bae6fd',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Products', route: '/product', visible: true },
    { label: 'Contact', route: '/contact', visible: true }
  ],
  legalLinks: [
    { label: 'Privacy Policy', route: '/privacy', visible: true },
    { label: 'Terms', route: '/terms', visible: true }
  ],
  socialLinks: [
    { label: 'Facebook', route: 'https://www.facebook.com/', visible: true },
    { label: 'Instagram', route: 'https://www.instagram.com/', visible: true }
  ],
  contact: { phone: '+1 (555) 555-1200', email: 'hello@retailstore.com', address: 'Retail Store' }
};

const retailDefaultTemplate = {
  primaryId: -3001,
  templateKey: 'RETAIL_DEFAULT',
  name: 'Retail Store Default',
  description: 'Default Retail storefront template',
  lifecycleStatus: 'PUBLISHED',
  version: 1,
  systemDefault: true,
  baseTemplateKey: 'SecondRetailTemplate',
  company: null,
  domain: { type: 'RETAIL' },
  attributes: {
    schemaVersion: 1,
    sourceDomain: 'RETAIL',
    sourceTemplate: 'SecondRetailTemplate',
    sourceComponent: 'RetailTemplateRenderer',
    runtime: { mode: 'structured', renderer: 'retail', renderMode: 'legacy-parity' },
    theme: retailTheme,
    header: { ...retailHeader, mode: 'legacy' },
    footer: { ...retailFooter, mode: 'legacy' },
    pageMeta: {}
  },
  pages: [
    { pageKey: 'home', route: '/', title: 'Retail Store', enabled: true, sections: [legacySection('retail-home', 1, 'SecondRetailTemplate', 'retailHome')] },
    { pageKey: 'products', route: '/product', title: 'Products', enabled: true, sections: [legacySection('retail-products', 1, 'ProductHome', 'retailProducts')] },
    { pageKey: 'category', route: '/category', title: 'Category Products', enabled: true, sections: [legacySection('retail-category', 1, 'CategoryProducts', 'retailCategory')] },
    { pageKey: 'productDetail', route: '/product-info', title: 'Product Detail', enabled: true, sections: [legacySection('retail-product-detail', 1, 'ProductDetails', 'retailProductDetail')] },
    { pageKey: 'cart', route: '/cart', title: 'Cart', enabled: true, sections: [legacySection('retail-cart', 1, 'RetailCart', 'retailCart')] },
    { pageKey: 'summary', route: '/order-history', title: 'Order Summary', enabled: true, sections: [legacySection('retail-summary', 1, 'OrderHistory', 'retailSummary')] },
    { pageKey: 'about', route: '/about', title: 'About', enabled: true, sections: [legacySection('retail-about', 1, 'About', 'retailAbout')] },
    { pageKey: 'contact', route: '/contact', title: 'Contact', enabled: true, sections: [legacySection('retail-contact', 1, 'Contact', 'retailContact')] },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', enabled: true, sections: [legacySection('retail-sign-in', 1, 'Login', 'authSignIn')] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', enabled: true, sections: [legacySection('retail-sign-up', 1, 'Signup', 'authSignUp')] }
  ],
  dynamicAttributes: [{ name: 'templateKey', value: 'RETAIL_DEFAULT' }]
};

const waasImages = {
  HERO: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=85&fit=crop&auto=format',
  STORY: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80&fit=crop&auto=format',
  SERVICE: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&q=80&fit=crop&auto=format',
  SERVER: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&q=85&fit=crop&auto=format',
  CAREER: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80&fit=crop&auto=format',
  AI_INSIGHTS: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80&fit=crop&auto=format',
  GALLERY_ONE: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80&fit=crop&auto=format',
  GALLERY_TWO: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80&fit=crop&auto=format',
  GALLERY_THREE: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80&fit=crop&auto=format'
};

const waasTheme = {
  primary: '#07111f',
  secondary: '#d8c2b6',
  accent: '#0b0f16',
  background: '#e6e8eb',
  surface: '#ffffff',
  color: '#0b0f16',
  mutedColor: '#5f6670',
  borderColor: '#d5d8dd',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  baseFontSize: '14px',
  headingWeight: '300',
  lineHeight: '1.55',
  pageWidth: '1120px',
  sectionSpacing: '72px 24px',
  buttonRadius: '999px',
  buttonHeight: '42px',
  cardRadius: '24px',
  cardShadow: '0 18px 42px rgba(24, 34, 46, 0.10)',
  inputRadius: '14px',
  imageRadius: '24px',
  footerBackground: '#e6e8eb'
};

const waasHeader = {
  visible: true,
  sticky: true,
  overlay: true,
  positionMode: 'overlay',
  height: '48px',
  backgroundColor: 'transparent',
  backgroundGradient: 'linear-gradient(180deg, rgba(8, 30, 42, .24), rgba(8, 30, 42, 0))',
  maxWidth: '1200px',
  brandInitial: 'A',
  brandName: 'AVPS Tech',
  brandColor: '#ffffff',
  navAlignment: 'right',
  navStyle: 'pill',
  ctaLabel: 'Contact us',
  ctaUrl: '/contactus',
  hideCta: true,
  nav: [
    { label: 'Home', route: '/', icon: 'Home', visible: true },
    { label: 'Services', route: '/services', icon: 'Services', visible: true },
    { label: 'Server', route: '/server', icon: 'Cloud', visible: true },
    { label: 'About', route: '/about', icon: 'Info', visible: true },
    { label: 'Career', route: '/careers', icon: 'Briefcase', visible: true },
    { label: 'Contact Us', route: '/contactus', icon: 'Mail', visible: true },
    { label: 'Sign In', route: '/sign-in', icon: 'LogIn', visible: false },
    { label: 'Sign Up', route: '/sign-up', icon: 'UserPlus', visible: false }
  ]
};

const waasFooter = {
  visible: true,
  backgroundColor: '#e6e8eb',
  textColor: '#4f5660',
  headingColor: '#0b0f16',
  linkColor: '#0b0f16',
  newsletterTitle: 'Let us build the systems behind your next digital move.',
  newsletterSubtitle: 'Get updates on services, careers, and digital transformation ideas.',
  newsletterPlaceholder: 'work@company.com',
  newsletterButtonLabel: 'START',
  newsletterConsentLabel: 'YES, CONTACT ME ABOUT IT SERVICES.',
  copyrightText: '(c) 2026 AVPS Tech. All rights reserved.',
  madeWithText: 'Software, cloud, servers, automation, and managed IT.',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Services', route: '/services', visible: true },
    { label: 'Server', route: '/server', visible: true },
    { label: 'Careers', route: '/careers', visible: true },
    { label: 'Contact', route: '/contactus', visible: true }
  ],
  contact: { phone: '+1 (555) 400-1020', email: 'hello@avpstech.com', address: 'Cloud and software delivery' }
};

const waasHeroSection = (order = 1) => domainSection(
  'waas-home-hero',
  'waasHero',
  order,
  { align: 'left', width: 'full', columns: '1', stackOnMobile: true },
  domainStyle({ backgroundColor: '#2f6f8f', padding: '0', minHeight: '640px', accentColor: '#07111f' }),
  {
    badgeLabel: 'AVPS Tech',
    eyebrow: 'Cloud, software, and managed IT',
    title: 'Designing digital systems that shape operations',
    subtitle: 'Architecture, cloud, server operations, and software delivery shaped into calm digital experiences.',
    imageUrl: waasImages.HERO,
    imageContent: { storedLink: '', displayLink: waasImages.HERO },
    statValue: '24/7',
    statLabel: 'infrastructure support, deployment guidance, and platform monitoring',
    actions: [action('Explore Services', '/services'), action('Server Infrastructure', '/server', 'outline')]
  }
);

const waasSimpleSection = (sectionId, type, order, content = {}, style = {}, layout = {}) => domainSection(
  sectionId,
  type,
  order,
  { align: 'left', width: 'contained', stackOnMobile: true, ...layout },
  domainStyle({ backgroundColor: '#ffffff', padding: '76px 20px', ...style }),
  content
);

const waasServicesGridSection = (order = 1) => waasSimpleSection('waas-services-grid', 'waasServicesGrid', order, {
  eyebrow: 'SELECTED SERVICES',
  title: 'Selected Services',
  subtitle: 'A focused service stack for organizations that need dependable platforms, connected workflows, and infrastructure that can keep up.',
  metrics: [
    { label: 'Platform', value: 'Cloud' },
    { label: 'Delivery', value: '2026' },
    { label: 'Client', value: 'Teams' }
  ],
  blocks: [
    block('Platform Design', 'A calm foundation for portals, workflows, APIs, and business operations.', '01', { badge: 'Cloud UI', imageUrl: waasImages.SERVICE, imageContent: { storedLink: '', displayLink: waasImages.SERVICE }, buttonLabel: 'Read more', url: '/services' }),
    block('Server Estate', 'Hybrid and cloud infrastructure planned around uptime, security, and ownership.', '02', { badge: 'Infra', imageUrl: waasImages.SERVER, imageContent: { storedLink: '', displayLink: waasImages.SERVER }, buttonLabel: 'View detail', url: '/server' }),
    block('Automation Flow', 'Connected approvals, alerts, reporting, and operational routines.', '03', { badge: 'Workflow', imageUrl: waasImages.AI_INSIGHTS, imageContent: { storedLink: '', displayLink: waasImages.AI_INSIGHTS }, buttonLabel: 'Read more', url: '/services' }),
    block('Data Layer', 'Dashboards, integrations, and decision support that teams can trust.', '04', { badge: 'Data', imageUrl: waasImages.GALLERY_ONE, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_ONE }, buttonLabel: 'Read more', url: '/services' }),
    block('Cloud Horizon', 'Migration and modernization paths for applications that need room to grow.', '05', { badge: 'Cloud', imageUrl: waasImages.GALLERY_TWO, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_TWO }, buttonLabel: 'Read more', url: '/services' }),
    block('Support Estate', 'Managed service practices for release support, monitoring, and maintenance.', '06', { badge: 'Support', imageUrl: waasImages.CAREER, imageContent: { storedLink: '', displayLink: waasImages.CAREER }, buttonLabel: 'Read more', url: '/contactus' })
  ]
}, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '3' });

const waasAboutStorySection = (order = 1, overlapHero = false) => waasSimpleSection('waas-about-story', 'waasAboutStory', order, {
  eyebrow: 'About AVPS Tech',
  title: 'Systems that speak',
  subtitle: 'Our work is rooted in the business experience, crafting systems that enable connection, invite interaction, and leave lasting operational impressions.',
  body: 'From application modernization to server operations, every layer is planned to feel clear, secure, and practical.',
  imageUrl: waasImages.STORY,
  imageContent: { storedLink: '', displayLink: waasImages.STORY },
  imageAlt: 'Technology team planning a digital platform',
  primaryActionLabel: 'ABOUT US',
  primaryActionUrl: '/about',
  blocks: [
    block('Contemporary Visioning', 'Creating modern technology spaces that feel warm and personal.'),
    block('Reliable System Balance', 'Combining vision and detail to perfect every production layer.'),
    block('Customized Delivery', 'Creating platforms that reflect each client workflow.', '', { imageUrl: waasImages.SERVICE, imageContent: { storedLink: '', displayLink: waasImages.SERVICE } }),
    block('Elegant IT Operations', 'Transforming support into daily confidence.', '', { imageUrl: waasImages.SERVER, imageContent: { storedLink: '', displayLink: waasImages.SERVER } })
  ]
}, { backgroundColor: '#e6e8eb', padding: overlapHero ? '0 24px 72px' : '68px 24px', ...(overlapHero ? { marginTop: '-112px' } : {}) }, { columns: '2' });

const waasPhilosophySection = (order = 1) => waasSimpleSection('waas-philosophy', 'waasPhilosophy', order, {
  title: 'Beyond the server room',
  quote: 'Good technology should feel almost invisible: dependable, observable, secure, and ready for the next idea.',
  author: 'AVPS Tech'
}, { backgroundColor: '#e6e8eb' });

const waasProcessSection = (order = 1) => waasSimpleSection('waas-process', 'waasProcessSteps', order, {
  eyebrow: 'HOW IT WORKS',
  title: 'A clear path from idea to operation.',
  subtitle: 'Every engagement moves through discovery, architecture, delivery, and support without losing sight of business outcomes.',
  blocks: [
    block('Discover', 'Map goals, users, systems, servers, risks, and business priorities.', '01', { badge: 'Step one' }),
    block('Design', 'Shape architecture, delivery milestones, security, and infrastructure choices.', '02', { badge: 'Step two' }),
    block('Operate', 'Ship, monitor, support, and improve the platform after launch.', '03', { badge: 'Step three' })
  ]
}, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '3' });

const waasServiceDetailSection = (order = 1) => waasSimpleSection('waas-service-detail', 'waasServiceDetail', order, {
  anchorId: 'server-infrastructure',
  eyebrow: 'SERVER & CLOUD INFRASTRUCTURE',
  title: 'Server and cloud infrastructure built for scale.',
  subtitle: 'Plan, migrate, secure, and operate the infrastructure layer behind high-availability products, internal platforms, and client-facing portals.',
  imageUrl: waasImages.SERVER,
  imageContent: { storedLink: '', displayLink: waasImages.SERVER },
  visualTitle: 'Production-grade cloud estate',
  visualSubtitle: 'Network, compute, storage, observability, and release controls aligned before workloads move.',
  primaryActionLabel: 'Talk infrastructure',
  primaryActionUrl: '/contactus',
  metrics: [
    { value: '99.9%', label: 'uptime planning', visible: true },
    { value: 'Zero Trust', label: 'security posture', visible: true },
    { value: '24/7', label: 'operational visibility', visible: true }
  ],
  highlights: [
    block('Cloud Readiness', 'Workload mapping, cost posture, migration risks, and target environments clarified before implementation.', '01'),
    block('Secure Migration', 'Staged runbooks for databases, applications, DNS, access, backups, and rollback planning.', '02'),
    block('Managed Operations', 'Monitoring, patching, incident response, ownership models, and support routines designed for continuity.', '03'),
    block('Release Systems', 'CI/CD, deployment gates, environment strategy, and observability connected into one delivery flow.', '04')
  ],
  blocks: [
    block('Cloud Architecture', 'Assess workloads, environments, security needs, cost controls, and migration constraints.', '01', { imageUrl: waasImages.GALLERY_ONE, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_ONE }, url: '/server' }),
    block('Managed Server Operations', 'Define patching, observability, backups, incident response, and ownership.', '02', { imageUrl: waasImages.SERVER, imageContent: { storedLink: '', displayLink: waasImages.SERVER }, url: '/server' }),
    block('Deployment & Observability', 'Connect infrastructure, CI/CD, release controls, telemetry, and support workflows.', '03', { imageUrl: waasImages.GALLERY_THREE, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_THREE }, url: '/server' })
  ]
}, { padding: '84px 24px', accentColor: '#c9a46a' }, { columns: '2' });

const waasPricingSection = (order = 1) => waasSimpleSection('waas-pricing', 'waasPricingPlans', order, {
  eyebrow: 'ENGAGEMENTS',
  title: 'Service packages for modern IT teams.',
  subtitle: 'Start with discovery, deliver a defined project, or keep systems healthy through ongoing support.',
  disclaimer: 'Replace these examples with your own offers, pricing model, or consultation paths.',
  blocks: [
    block('Automation Package', 'Business process automation, rule-driven workflows, and implementation support.', '01', { price: 'Plan', period: 'automation', features: ['Workflow discovery', 'Rules and approvals', 'Business user enablement'], buttonLabel: 'Learn more', url: '/services' }),
    block('Server & Cloud Package', 'Cloud migration, containerization, infrastructure planning, and deployment support.', '02', { price: 'Plan', period: 'infrastructure', badge: 'Server', features: ['Cloud readiness review', 'Secure migration path', 'Deployment best practices'], buttonLabel: 'Explore', url: '/services#server-infrastructure', backgroundColor: '#f4f5f6' }),
    block('Managed IT Package', 'Ongoing application support, monitoring, maintenance, and delivery assistance.', '03', { price: 'Plan', period: 'support', features: ['Application maintenance', 'Issue triage', 'Continuous improvement'], buttonLabel: 'Contact us', url: '/contactus' })
  ]
}, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '3' });

const waasFaqSection = (order = 1) => waasSimpleSection('waas-faq', 'waasFaq', order, {
  eyebrow: 'QUESTIONS',
  title: 'Common questions before working with your team.',
  subtitle: 'Use these defaults for services, careers, product implementation, contact, or onboarding content.',
  blocks: [
    block('Can your team join an in-progress project?', 'Yes. Use this answer to explain how your team supports discovery, development, QA, production, and maintenance stages.'),
    block('Do you provide managed IT services?', 'Yes. Describe your application support, monitoring, maintenance, and infrastructure coverage here.'),
    block('Do you help with cloud migration?', 'Yes. Explain your approach for cloud readiness, architecture, migration planning, and deployment.'),
    block('Can clients customize this engagement?', 'Yes. Use this answer to clarify consultation, package, or service customization options.')
  ]
}, { backgroundColor: '#e6e8eb', padding: '68px 24px' }, { columns: '2' });

const waasTestimonialsSection = (order = 1) => waasSimpleSection('waas-testimonials', 'waasTestimonials', order, {
  eyebrow: 'CLIENT VOICES',
  title: 'Voices from those we have built with.',
  subtitle: 'Our mixed technology team handled complex needs, balanced bold architecture with real-world constraints, and delivered an efficient final platform.',
  blocks: [
    block('Jean Sricelle', 'Platform Director', '', { badge: 'Cloud', quote: 'Visionary systems design with practical execution.', imageUrl: waasImages.STORY, imageContent: { storedLink: '', displayLink: waasImages.STORY } }),
    block('Jordan Mills', 'CTO', '', { badge: 'Servers', quote: 'Infrastructure choices became clearer and easier to trust.', imageUrl: waasImages.CAREER, imageContent: { storedLink: '', displayLink: waasImages.CAREER } }),
    block('Mira Shah', 'Operations Lead', '', { badge: 'Automation', quote: 'The team turned scattered workflows into a platform people use.', imageUrl: waasImages.GALLERY_THREE, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_THREE } }),
    block('Alex Chen', 'Founder', '', { badge: 'Data', quote: 'Delivery stayed practical from concept through launch.', imageUrl: waasImages.AI_INSIGHTS, imageContent: { storedLink: '', displayLink: waasImages.AI_INSIGHTS } }),
    block('Sam Rivera', 'Product Lead', '', { badge: 'Support', quote: 'Support and monitoring finally felt connected.', imageUrl: waasImages.SERVER, imageContent: { storedLink: '', displayLink: waasImages.SERVER } })
  ]
}, { backgroundColor: '#e6e8eb', padding: '78px 24px' }, { columns: '3' });

const waasGallerySection = (order = 1) => waasSimpleSection('waas-gallery', 'waasImageGallery', order, {
  eyebrow: 'BEYOND THE BLUEPRINT',
  title: 'Beyond the Blueprint',
  subtitle: 'Exploring ideas, patterns, and innovations that shape the systems teams run every day.',
  blocks: [
    block('Designing Systems That Inspire', 'Technology is more than servers and code. It is about shaping experiences as businesses evolve and operating models change.', '', { imageUrl: waasImages.GALLERY_TWO, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_TWO }, badge: 'Article' }),
    block('Cloud Computing', 'Deploying platforms onto the cloud.', '', { imageUrl: waasImages.GALLERY_ONE, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_ONE }, badge: 'Cloud' }),
    block('DevOps Tooling', 'Developing and supporting enterprise applications using DevOps tools.', '', { imageUrl: waasImages.GALLERY_THREE, imageContent: { storedLink: '', displayLink: waasImages.GALLERY_THREE }, badge: 'DevOps' })
  ]
}, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '3' });

const waasCareerHeroSection = (order = 1) => waasSimpleSection('waas-career-hero', 'waasCareerHero', order, {
  eyebrow: 'Careers',
  title: 'Build useful technology with us.',
  subtitle: 'Join teams working across software, cloud, server operations, automation, and customer success.',
  imageUrl: waasImages.CAREER,
  imageContent: { storedLink: '', displayLink: waasImages.CAREER },
  actions: [action('See open roles', '/careers', 'primary'), action('Email us', '/contactus', 'outline')]
}, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '2' });

const waasOpenRolesSection = (order = 1) => waasSimpleSection('waas-open-roles', 'waasOpenRoles', order, {
  eyebrow: 'OPEN ROLES',
  title: 'Current openings',
  subtitle: 'Use these cards for current openings, role expectations, locations, and application instructions.',
  blocks: [
    block('Full Stack Developer', 'Develop, validate, automate, and create quality solutions focused on client business needs using modern frameworks, CI/CD, and cloud-aware engineering practices.', '', { badge: 'Full Time', location: 'Add application details' }),
    block('Software Engineer', 'Develop, customize, and implement enterprise applications using your preferred languages, platforms, cloud services, and delivery tooling.', '', { badge: 'Full Time', location: 'Add location' })
  ]
}, { backgroundColor: '#e6e8eb', padding: '68px 24px' }, { columns: '3' });

const waasContactSection = (order = 1) => waasSimpleSection('waas-contact', 'waasContact', order, {
  eyebrow: 'CONTACT US',
  title: 'Let us build your vision together.',
  subtitle: 'Share your software, cloud, server, or automation goals and we will shape the next practical step.',
  phoneTitle: 'Phone',
  phoneValue: '+1 (555) 400-1020',
  emailTitle: 'Email',
  emailValue: 'hello@avpstech.com',
  phoneHours: 'Business hours',
  addressTitle: 'Office',
  addressValue: 'Add your office address\nCity, State ZIP\nCountry',
  formTitle: 'Get in Touch',
  submitLabel: 'SUBMIT MESSAGE',
  socialLinks: [
    { label: 'LinkedIn', route: '', icon: 'Linkedin', platformKey: 'linkedin', visible: true },
    { label: 'YouTube', route: '', icon: 'Youtube', platformKey: 'youtube', visible: true },
    { label: 'Facebook', route: '', icon: 'Facebook', platformKey: 'facebook', visible: true },
    { label: 'X', route: '', icon: 'Twitter', platformKey: 'twitter', visible: true }
  ],
  fields: [
    { label: 'Name *', type: 'text', required: true, visible: true },
    { label: 'Company', type: 'text', required: false, visible: true },
    { label: 'Email *', type: 'email', required: true, visible: true },
    { label: 'Phone Number', type: 'tel', required: false, visible: true },
    { label: 'Message *', type: 'textarea', required: true, visible: true }
  ]
}, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '2' });

const waasAuthSection = (order = 1, mode = 'signIn') => waasSimpleSection(`waas-${mode}`, 'waasAuth', order, {
  mode,
  eyebrow: mode === 'signUp' ? 'CREATE YOUR ACCOUNT' : 'CLIENT PORTAL',
  title: mode === 'signUp' ? 'Create your technology account.' : 'Sign in to your client portal.',
  subtitle: mode === 'signUp'
    ? 'Create an account to keep service conversations, onboarding details, and engagement updates in one place.'
    : 'Sign in to access project updates, service requests, onboarding details, and account information.',
  formTitle: mode === 'signUp' ? 'Create account' : 'Sign in',
  submitLabel: mode === 'signUp' ? 'CREATE ACCOUNT' : 'SIGN IN',
  alternateLabel: mode === 'signUp' ? 'Already have an account? Sign in' : 'Need an account? Sign up',
  alternateUrl: mode === 'signUp' ? '/sign-in' : '/sign-up',
  imageUrl: mode === 'signUp' ? waasImages.GALLERY_THREE : waasImages.SERVER,
  imageContent: { storedLink: '', displayLink: mode === 'signUp' ? waasImages.GALLERY_THREE : waasImages.SERVER },
  imageAlt: mode === 'signUp' ? 'Technology deployment and onboarding' : 'Technology services client portal',
  fields: mode === 'signUp' ? [
    { label: 'First Name', type: 'text', required: true, visible: true },
    { label: 'Last Name', type: 'text', required: true, visible: true },
    { label: 'Email', type: 'email', required: true, visible: true },
    { label: 'Phone Number', type: 'tel', required: false, visible: true },
    { label: 'Password', type: 'password', required: true, visible: true }
  ] : [
    { label: 'Email', type: 'email', required: true, visible: true },
    { label: 'Password', type: 'password', required: true, visible: true }
  ],
  blocks: [
    block('Cloud implementation support', 'Keep service conversations connected to your delivery team.', '01'),
    block('Managed IT service access', 'Use one account entry point for support, onboarding, and follow-up.', '02')
  ]
}, { backgroundColor: '#e6e8eb', padding: '78px 24px' }, { columns: '2' });

const waasNewsletterSection = (order = 1) => waasSimpleSection('waas-newsletter', 'waasNewsletterFooter', order, {
  title: 'Let us Build Your Vision Together',
  subtitle: 'From concept to creation, our team is ready to design systems that support your next move.',
  placeholder: 'work@company.com',
  buttonLabel: 'START YOUR PROJECT',
  buttonUrl: '/contactus',
  imageUrl: waasImages.HERO,
  imageContent: { storedLink: '', displayLink: waasImages.HERO },
  consentLabel: 'YES, CONTACT ME ABOUT IT SERVICES.'
}, { backgroundColor: '#f4f5f6', padding: '56px 24px' });

const waasDefaultTemplate = {
  primaryId: -4001,
  templateKey: 'WAAS_DEFAULT',
  name: 'WaaS IT Services Default',
  description: 'Default WaaS IT services template',
  lifecycleStatus: 'PUBLISHED',
  version: 1,
  systemDefault: true,
  baseTemplateKey: 'WaasAvpsTemplate',
  company: null,
  domain: { type: 'WAAS' },
  attributes: {
    schemaVersion: 5,
    sourceDomain: 'WAAS',
    sourceTemplate: 'WaasAvpsTemplate',
    sourceComponent: 'WaasTemplateRenderer',
    runtime: { mode: 'structured', renderer: 'waas', readerScale: '1' },
    waasReaderScaleVersion: 4,
    theme: waasTheme,
    header: waasHeader,
    footer: waasFooter,
    pageMeta: {}
  },
  pages: [
    { pageKey: 'home', route: '/', title: 'Home', enabled: true, sections: [waasHeroSection(1), waasAboutStorySection(2, true), waasServicesGridSection(3), waasTestimonialsSection(4), waasServiceDetailSection(5), waasGallerySection(6), waasNewsletterSection(7)] },
    { pageKey: 'about', route: '/about', title: 'About', enabled: true, sections: [waasAboutStorySection(1), waasSimpleSection('waas-expertise-spotlight', 'waasInstructorSpotlight', 2, { eyebrow: 'TECHNICAL EXPERTISE', title: 'Cloud, DevOps, data, and software engineering in one delivery motion.', subtitle: 'Use this section to list your strongest platforms, frameworks, cloud providers, DevOps tooling, programming languages, and data capabilities.', imageUrl: waasImages.AI_INSIGHTS, imageContent: { storedLink: '', displayLink: waasImages.AI_INSIGHTS }, imageAlt: 'AI and predictive technology illustration', primaryActionLabel: 'Learn about us', primaryActionUrl: '/about', blocks: [block('Platform expertise', 'Add your strongest enterprise platforms, cloud services, and integration tools.', '01'), block('Cloud-native delivery', 'Highlight container platforms, cloud services, data stores, and deployment models.', '02'), block('Engineering tooling', 'List your DevOps, CI/CD, automation, testing, and software development tools.', '03')] }, { backgroundColor: '#e6e8eb', padding: '72px 24px' }, { columns: '2' }), waasPhilosophySection(3), waasProcessSection(4), waasGallerySection(5), waasNewsletterSection(6)] },
    { pageKey: 'services', route: '/services', title: 'Services', enabled: true, sections: [waasServicesGridSection(1), waasServiceDetailSection(2), waasPricingSection(3), waasSimpleSection('waas-schedule', 'waasSchedule', 4, { eyebrow: 'START A PROJECT', title: 'Start with a focused technology conversation.', subtitle: 'Clarify your software, automation, cloud migration, server operations, or managed support needs.', timezoneLabel: 'Remote and on-site assistance available', primaryActionLabel: 'Contact us', primaryActionUrl: '/contactus', scheduleOptions: [{ title: 'Consultation session', description: 'Identify business rules, technical state, and project goals.', date: '01', time: 'Plan', badge: 'Consult', buttonLabel: 'Start', url: '/contactus', visible: true }, { title: 'Solution roadmap', description: 'Define implementation, modernization, migration, or managed-service steps.', date: '02', time: 'Design', badge: 'Roadmap', buttonLabel: 'Discuss', url: '/contactus', visible: true }, { title: 'Delivery support', description: 'Move from architecture and development into deployment, maintenance, and enablement.', date: '03', time: 'Build', badge: 'Deliver', buttonLabel: 'Connect', url: '/contactus', visible: true }] }, { backgroundColor: '#f4f5f6', padding: '68px 24px' }), waasFaqSection(5), waasNewsletterSection(6)] },
    { pageKey: 'server', route: '/server', title: 'Server', enabled: true, sections: [waasServiceDetailSection(1), waasServicesGridSection(2), waasProcessSection(3), waasSimpleSection('waas-schedule', 'waasSchedule', 4, { eyebrow: 'START A PROJECT', title: 'Start with a focused technology conversation.', subtitle: 'Clarify your software, automation, cloud migration, server operations, or managed support needs.', timezoneLabel: 'Remote and on-site assistance available', primaryActionLabel: 'Contact us', primaryActionUrl: '/contactus', scheduleOptions: [{ title: 'Consultation session', description: 'Identify business rules, technical state, and project goals.', date: '01', time: 'Plan', badge: 'Consult', buttonLabel: 'Start', url: '/contactus', visible: true }, { title: 'Solution roadmap', description: 'Define implementation, modernization, migration, or managed-service steps.', date: '02', time: 'Design', badge: 'Roadmap', buttonLabel: 'Discuss', url: '/contactus', visible: true }, { title: 'Delivery support', description: 'Move from architecture and development into deployment, maintenance, and enablement.', date: '03', time: 'Build', badge: 'Deliver', buttonLabel: 'Connect', url: '/contactus', visible: true }] }, { backgroundColor: '#f4f5f6', padding: '68px 24px' }), waasNewsletterSection(5)] },
    { pageKey: 'career', route: '/careers', title: 'Career', enabled: true, sections: [waasCareerHeroSection(1), waasOpenRolesSection(2), waasProcessSection(3), waasTestimonialsSection(4), waasNewsletterSection(5)] },
    { pageKey: 'contact', route: '/contactus', title: 'Contact', enabled: true, sections: [waasContactSection(1), waasFaqSection(2), waasNewsletterSection(3)] },
    { pageKey: 'signIn', route: '/sign-in', title: 'Sign In', enabled: true, sections: [waasAuthSection(1, 'signIn'), waasFaqSection(2)] },
    { pageKey: 'signUp', route: '/sign-up', title: 'Sign Up', enabled: true, sections: [waasAuthSection(1, 'signUp'), waasNewsletterSection(2)] }
  ],
  dynamicAttributes: [{ name: 'templateKey', value: 'WAAS_DEFAULT' }]
};

const defaultTemplatesByDomain = {
  EDUCATION: educationDefaultTemplate,
  RETAIL: retailDefaultTemplate,
  FLEX_OFFICE: flexOfficeDefaultTemplate,
  WAAS: waasDefaultTemplate
};

const routeAliasesByDomain = {
  EDUCATION: {
    home: ['/'],
    products: ['/course', '/courses'],
    courseDetail: ['/course-detail', '/course-detail/:name', '/courseDetails', '/courseDetails/:name'],
    cart: ['/cart'],
    summary: ['/order-history', '/my-orders', '/orders', '/profile/myorders'],
    about: ['/about'],
    aboutAcademy: ['/about-academy'],
    contact: ['/contact', '/contactus'],
    bridgingKnowledgeGap: ['/BridgingTheKnowledgeGap', '/bridging-the-knowledge-gap', '/bridging-knowledge-gap'],
    insightsEngagement: ['/InsightsAndEngagement', '/insights-engagement', '/insights-and-engagement'],
    industryExperience: ['/BuildIndustryExperience', '/industry-experience', '/build-industry-experience'],
    groomingSkills: ['/GroomingSkills', '/grooming-skills'],
    signIn: ['/sign-in', '/login'],
    signUp: ['/sign-up', '/signup', '/register']
  },
  RETAIL: {
    home: ['/'],
    products: ['/product', '/products'],
    category: ['/category', '/category/:name', '/category/sf/:name', '/category/tf/:name'],
    productDetail: ['/product-info', '/at/product-info', '/at/service', '/sf/product-info', '/tf/product-info'],
    cart: ['/cart', '/sf/cart', '/at/cart'],
    summary: ['/order-history', '/my-orders', '/orders', '/profile/myorders'],
    about: ['/about'],
    contact: ['/contact', '/contactus'],
    signIn: ['/sign-in', '/login'],
    signUp: ['/sign-up', '/signup', '/register']
  },
  FLEX_OFFICE: {
    home: ['/'],
    bookingHome: ['/flex-home', '/booking-home'],
    bookSpace: ['/book-space'],
    spaceTypes: ['/type-of-spaces'],
    exploreLocations: ['/explore-locations'],
    bookSchedule: ['/book-schedule'],
    choice: ['/choice'],
    viewService: ['/view-service'],
    services: ['/our-services', '/services'],
    serviceDetail: ['/selected-product-details', '/service-detail', '/service-detail/:name'],
    features: ['/our-features', '/features'],
    marketingFeatures: ['/features'],
    confirmBook: ['/confirm-book'],
    reviewSchedule: ['/review-schedule'],
    bookings: ['/my-bookings', '/bookings'],
    orders: ['/my-orders', '/user-profile/my-orders'],
    customerOrders: ['/customer-orders'],
    userCustomerOrders: ['/user-profile/customer-orders'],
    invoice: ['/my-invoice', '/invoice'],
    invoiceDetail: ['/my-invoice/:id'],
    aboutFlexOffice: ['/about-flexoffice'],
    contact: ['/contact', '/contactus'],
    signIn: ['/sign-in', '/login'],
    signUp: ['/sign-up', '/signup', '/register']
  },
  WAAS: {
    home: ['/', '/home'],
    about: ['/about'],
    services: ['/services', '/our-services'],
    server: ['/server', '/serve', '/services#server-infrastructure'],
    career: ['/careers', '/career', '/carrer'],
    contact: ['/contactus', '/contact'],
    signIn: ['/sign-in', '/login'],
    signUp: ['/sign-up', '/signup', '/register']
  }
};

const normalizeRoute = (route = '') => {
  if (!route) return '';
  const rawRoute = String(route).trim();
  if (!rawRoute) return '';

  let pathname = rawRoute;
  try {
    pathname = new URL(rawRoute, 'https://avps.local').pathname;
  } catch (error) {
    pathname = rawRoute.split(/[?#]/)[0];
  }

  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
};

const routeMatches = (route, candidate) => {
  const current = normalizeRoute(route).toLowerCase();
  const target = normalizeRoute(candidate).toLowerCase();
  if (!current || !target) return false;
  if (current === target) return true;

  const currentParts = current.split('/').filter(Boolean);
  const targetParts = target.split('/').filter(Boolean);
  if (currentParts.length !== targetParts.length) return false;

  return targetParts.every((part, index) => part.startsWith(':') || part === currentParts[index]);
};

const pageAliasesFor = (domainType, pageKey) => (
  routeAliasesByDomain[normalizeTemplateDomain(domainType)]?.[pageKey] || []
);

const compactPageKey = (value = '') => String(value || '').replace(/[\s_-]+/g, '').toLowerCase();
const pageKeyMatches = (actual, requested) => (
  Boolean(requested)
  && compactPageKey(actual) === compactPageKey(requested)
);

export const getDefaultTemplateForDomain = (domainType) => {
  const template = defaultTemplatesByDomain[normalizeTemplateDomain(domainType)];
  return template ? clone(template) : null;
};

const activeTemplateApiStateKey = (companyId, domainType) => (
  `${ACTIVE_TEMPLATE_API_STATE_PREFIX}:${companyId || ''}:${normalizeTemplateDomain(domainType) || domainType || ''}`
);

export const setActiveTemplateApiState = (companyId, domainType, state = {}) => {
  try {
    const responseCount = Number.isFinite(Number(state.responseCount))
      ? Number(state.responseCount)
      : null;
    const nextState = {
      status: state.status || 'unknown',
      companyId: companyId || '',
      domainType: normalizeTemplateDomain(domainType) || domainType || '',
      responseCount,
      hasApiResponse: responseCount !== null ? responseCount > 0 : Boolean(state.hasApiResponse),
      hasPublishedTemplate: Boolean(state.hasPublishedTemplate),
      resolutionStatus: state.resolutionStatus || (state.status === 'success'
        ? (responseCount === 0 ? 'default' : state.hasPublishedTemplate ? 'assigned' : 'error')
        : 'error'),
      reason: state.reason || '',
      checkedAt: state.checkedAt || Date.now()
    };

    sessionStorage.setItem(activeTemplateApiStateKey(companyId, domainType), JSON.stringify(nextState));
    sessionStorage.setItem(ACTIVE_TEMPLATE_API_READY_KEY, JSON.stringify(nextState));
    window.dispatchEvent(new Event('avps-template-updated'));
  } catch {
  }
};

export const getActiveTemplateApiState = (companyId, domainType) => {
  try {
    const rawState = sessionStorage.getItem(activeTemplateApiStateKey(companyId, domainType));
    if (!rawState) return null;
    return JSON.parse(rawState);
  } catch {
    return null;
  }
};

const apiStateIsFreshForCurrentLoad = (state) => Boolean(
  state
  && (state.status === 'success' || state.status === 'error')
  && Number(state.checkedAt) >= ACTIVE_TEMPLATE_PAGE_LOAD_TS
);

const apiStateMatchesContext = (state, { companyId, domainType } = {}) => {
  if (!state) return false;
  const stateCompanyId = state.companyId;
  const stateDomainType = normalizeTemplateDomain(state.domainType);
  const requestedDomainType = normalizeTemplateDomain(domainType);
  const companyMatches = !companyId || !stateCompanyId || String(stateCompanyId) === String(companyId);
  const domainMatches = !requestedDomainType || !stateDomainType || stateDomainType === requestedDomainType;
  return companyMatches && domainMatches;
};

const getLatestActiveTemplateApiState = () => {
  try {
    const rawState = sessionStorage.getItem(ACTIVE_TEMPLATE_API_READY_KEY);
    if (!rawState) return null;
    return JSON.parse(rawState);
  } catch {
    return null;
  }
};

export const hasFreshActiveTemplateApiLoad = (companyId, domainType) => {
  const specificState = getActiveTemplateApiState(companyId, domainType);
  if (apiStateIsFreshForCurrentLoad(specificState)) return true;

  const latestState = getLatestActiveTemplateApiState();
  return apiStateIsFreshForCurrentLoad(latestState)
    && apiStateMatchesContext(latestState, { companyId, domainType });
};

export const shouldRenderDefaultTemplateForEmptyApiResponse = (companyId, domainType) => {
  const state = getActiveTemplateApiState(companyId, domainType);
  return state?.status === 'success' && Number(state.responseCount) === 0;
};

const domainOf = (template, fallback = '') => (
  normalizeTemplateDomain(template?.domain?.type || template?.attributes?.sourceDomain || fallback || '')
);

const mergeFooter = (defaultFooter = {}, footer = {}) => ({
  ...defaultFooter,
  ...footer,
  contact: {
    ...(defaultFooter.contact || {}),
    ...(footer.contact || {})
  }
});

const scaleWaasCssPx = (value = '') => (
  typeof value === 'string'
    ? value.replace(/(\d+(?:\.\d+)?)px/g, (_, size) => `${Math.round(Number(size) * 0.82)}px`)
    : value
);

const compactStoredWaasTemplate = (template) => {
  if (!template || template.attributes?.waasReaderScaleVersion === 4) return;

  template.attributes.theme = {
    ...(template.attributes.theme || {}),
    pageWidth: ['1180px', '1220px'].includes(template.attributes.theme?.pageWidth) ? '1120px' : template.attributes.theme?.pageWidth,
    sectionSpacing: ['96px 24px', '86px 30px'].includes(template.attributes.theme?.sectionSpacing) ? '72px 24px' : template.attributes.theme?.sectionSpacing,
    gridGap: ['28px', '22px', '20px'].includes(template.attributes.theme?.gridGap) ? '16px' : template.attributes.theme?.gridGap,
    containerPadding: ['30px', '24px', '20px'].includes(template.attributes.theme?.containerPadding) ? '24px' : template.attributes.theme?.containerPadding,
    headerHeight: ['72px', '64px', '58px'].includes(template.attributes.theme?.headerHeight) ? '48px' : template.attributes.theme?.headerHeight
  };
  template.attributes.header = {
    ...(template.attributes.header || {}),
    overlay: true,
    positionMode: 'overlay',
    height: ['72px', '64px', '58px'].includes(template.attributes.header?.height) ? '48px' : template.attributes.header?.height,
    backgroundColor: ['#2f6f8f', '#07111f'].includes(template.attributes.header?.backgroundColor) ? 'transparent' : template.attributes.header?.backgroundColor,
    backgroundGradient: template.attributes.header?.backgroundGradient || 'linear-gradient(180deg, rgba(8, 30, 42, .24), rgba(8, 30, 42, 0))',
    hideCta: true,
    nav: (template.attributes.header?.nav || []).map((item) => (
      ['/sign-in', '/sign-up'].includes(item?.route)
        ? { ...item, visible: false }
        : item?.route === '/contactus'
          ? { ...item, label: item.label === 'Contact' ? 'Contact Us' : item.label }
          : item
    ))
  };
  template.attributes.schemaVersion = Math.max(Number(template.attributes.schemaVersion || 0), 5);
  (template.pages || []).forEach((page) => {
    (page.sections || []).forEach((section) => {
      if (section?.style?.padding) section.style.padding = scaleWaasCssPx(section.style.padding);
      if (section?.style?.minHeight) section.style.minHeight = scaleWaasCssPx(section.style.minHeight);
    });
  });
  template.attributes.waasReaderScaleVersion = 4;
};

const legacyDefaultContentMarkersByDomain = {
  EDUCATION: {
    educationProductsPage: (content = {}) => (
      content.componentKey === 'Products'
      || content.title === 'Explore courses'
    ),
    educationAbout: (content = {}) => (
      content.title === 'About our academy'
    ),
    educationContact: (content = {}) => (
      content.title === 'Contact our learning team'
    )
  },
  FLEX_OFFICE: {
    flexOfficeBookingSearch: (content = {}) => (
      content.eyebrow === 'Book a workspace'
      && content.title === 'Find the right space for your next work session.'
      && content.primaryActionLabel === 'Search Availability'
    ),
    flexOfficeServiceDetail: (content = {}) => (
      content.eyebrow === 'Workspace Details'
      && content.subtitle === 'A premium meeting space with display, whiteboard, video conferencing, and seating for collaborative sessions.'
      && content.primaryActionUrl === '/confirm-book'
    ),
    flexOfficeBookings: (content = {}) => (
      content.title === 'My Bookings'
      && content.subtitle === 'Track upcoming reservations, payment status, invoices, and support details.'
    ),
    flexOfficeInvoice: (content = {}) => (
      content.title === 'Invoice Summary'
      && content.totalLabel === '$507.00'
      && content.statusLabel === 'Payment pending'
    ),
    flexOfficeContact: (content = {}) => (
      content.subtitle === 'Talk to our workspace team about availability, recurring bookings, invoices, or custom requirements.'
      || content.addressValue === '123 Workspace Ave, Suite 400'
    )
  }
};

const preserveAuthoredContentFields = (baseContent = {}, authoredContent = {}) => {
  const merged = { ...(baseContent || {}) };
  ['builderElements', 'canvasElements', 'canvasOverlay'].forEach((key) => {
    if (authoredContent[key] !== undefined) {
      merged[key] = clone(authoredContent[key]);
    }
  });
  return merged;
};

const mergeSectionDefaultsForReader = (section, defaultSection, domainType) => {
  if (!defaultSection) return section;
  const normalizedDomain = normalizeTemplateDomain(domainType);
  const content = section.content || {};
  const legacyDefaultContent = legacyDefaultContentMarkersByDomain[normalizedDomain]?.[section.type]?.(content);
  const mergedContent = legacyDefaultContent
    ? preserveAuthoredContentFields(clone(defaultSection.content || {}), content)
    : { ...(defaultSection.content || {}), ...content };

  return {
    ...section,
    layout: { ...(defaultSection.layout || {}), ...(section.layout || {}) },
    style: { ...(defaultSection.style || {}), ...(section.style || {}) },
    bindings: { ...(defaultSection.bindings || {}), ...(section.bindings || {}) },
    content: mergedContent
  };
};

const normalizePage = (page, defaultPage, domainType) => {
  const normalizedDomain = normalizeTemplateDomain(domainType);
  const pageSections = Array.isArray(page?.sections) ? page.sections : [];
  const legacyWrapper = pageSections.length === 1 && ['existingComponent', 'legacyComponent'].includes(pageSections[0]?.type);
  const useDefaultSections = legacyWrapper
    && normalizedDomain === 'EDUCATION'
    && ['products', 'courseDetail'].includes(page?.pageKey);
  const defaultSectionsByType = new Map((defaultPage?.sections || []).map((section) => [section.type, section]));
  const sections = pageSections.length && !useDefaultSections
    ? pageSections.map((section) => mergeSectionDefaultsForReader(section, defaultSectionsByType.get(section.type), domainType))
    : (defaultPage?.sections || []);

  return {
    ...(defaultPage || {}),
    ...(page || {}),
    attributes: {
      ...(defaultPage?.attributes || {}),
      ...(page?.attributes || {})
    },
    sections
  };
};

// Premium WAAS sites are authored as a self-contained page family (waasPremiumHome +
// waasAvpsInlinePage sections). They must render EXCLUSIVELY their authored pages: the
// legacy non-premium default WAAS pages (home/about/services/server/career/contact/…)
// must NOT be seeded alongside them, otherwise unauthored routes such as `/server` leak
// the old template and shared routes (`/careers`, `/contactus`) collide/overlap.
const PREMIUM_WAAS_SECTION_TYPES = new Set(['waasPremiumHome', 'waasAvpsInlinePage']);

const templateHasPremiumWaasSections = (template) => {
  const pages = Array.isArray(template?.pages) ? template.pages : [];
  return pages.some((page) => (
    Array.isArray(page?.sections)
    && page.sections.some((section) => PREMIUM_WAAS_SECTION_TYPES.has(section?.type))
  ));
};

const EXCLUSIVE_WAAS_TEMPLATE_STYLES = new Set(['premium', 'avps-pages']);

const isPremiumWaasTemplate = (template, domainType) => {
  if (!template || normalizeTemplateDomain(domainType) !== 'WAAS') return false;
  const style = String(template?.attributes?.waasTemplateStyle || '').trim().toLowerCase();
  if (EXCLUSIVE_WAAS_TEMPLATE_STYLES.has(style)) return true;
  return templateHasPremiumWaasSections(template);
};

export const normalizeTemplateForReader = (template, domainType) => {
  const sourceTemplate = extractTemplatePayload(template) || template;
  const resolvedDomain = normalizeTemplateDomain(domainType || domainOf(sourceTemplate));
  const defaultTemplate = resolvedDomain ? defaultTemplatesByDomain[resolvedDomain] : null;
  const base = sourceTemplate ? clone(sourceTemplate) : (defaultTemplate ? clone(defaultTemplate) : null);

  if (!base) return null;

  const preliminaryDomain = normalizeTemplateDomain(domainOf(base, resolvedDomain || domainOf(defaultTemplate)));
  const usesEducationExamTemplate = preliminaryDomain === 'EDUCATION' && isEducationExamTemplate(base);
  const variantDefaultTemplate = usesEducationExamTemplate ? educationExamTemplate : defaultTemplate;
  const defaultClone = variantDefaultTemplate ? clone(variantDefaultTemplate) : {};
  const finalDomain = normalizeTemplateDomain(domainOf(base, resolvedDomain || domainOf(defaultClone)));
  const defaultAttributes = defaultClone.attributes || {};
  const incomingAttributes = base.attributes || {};
  const mergedThemeAttributes = {
    ...incomingAttributes,
    theme: { ...(defaultAttributes.theme || {}), ...(incomingAttributes.theme || {}) }
  };

  if (usesEducationExamTemplate && Array.isArray(base.pages)) {
    const examHomePage = (educationExamTemplate.pages || []).find((page) => page.pageKey === 'home');
    base.pages = base.pages.map((page) => (
      isDefaultEducationLegacyHomePage(page) && examHomePage
        ? clone(examHomePage)
        : page
    ));
  }

  base.domain = { ...(base.domain || {}), type: finalDomain || base.domain?.type };
  base.attributes = {
    ...defaultAttributes,
    ...incomingAttributes,
    sourceDomain: incomingAttributes.sourceDomain || defaultAttributes.sourceDomain || finalDomain,
    ...(finalDomain === 'EDUCATION'
      ? { educationTemplateStyle: getEducationTemplateStyle(base) }
      : {}),
    runtime: { ...(defaultAttributes.runtime || {}), ...(incomingAttributes.runtime || {}) },
    theme: normalizeTemplateTheme({ ...base, attributes: mergedThemeAttributes }, finalDomain),
    header: { ...(defaultAttributes.header || {}), ...(incomingAttributes.header || {}) },
    footer: mergeFooter(defaultAttributes.footer, incomingAttributes.footer),
    pageMeta: { ...(defaultAttributes.pageMeta || {}), ...(incomingAttributes.pageMeta || {}) }
  };
  if (finalDomain === 'WAAS') {
    compactStoredWaasTemplate(base);
  }

  const defaultPagesByKey = new Map((defaultClone.pages || []).map((page) => [page.pageKey, page]));
  const pagesByKey = new Map();
  // A published company template's page list is its routing contract. Do not recreate
  // pages the author omitted or disabled (notably auth pages on brochure/company sites).
  // System defaults still receive the complete domain scaffold. The explicit routing
  // marker is written by the builder; the company/non-system check also protects older
  // published templates created before that marker existed.
  const usesConfiguredPages = incomingAttributes.pageRoutingMode === 'configured'
    || isPremiumWaasTemplate(base, finalDomain)
    || (
      !isSystemDefaultTemplate(base)
      && Array.isArray(sourceTemplate?.pages)
    );
  if (!usesConfiguredPages || usesEducationExamTemplate) {
    (defaultClone.pages || []).forEach((page) => pagesByKey.set(page.pageKey, normalizePage(null, page, finalDomain)));
  }
  (Array.isArray(base.pages) ? base.pages : []).forEach((page) => {
    pagesByKey.set(page.pageKey, normalizePage(page, defaultPagesByKey.get(page.pageKey), finalDomain));
  });
  base.pages = Array.from(pagesByKey.values());
  if (usesEducationExamTemplate) {
    base.pages = base.pages.map((page) => ({
      ...page,
      attributes: {
        ...(page.attributes || {}),
        educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM
      },
      sections: (page.sections || []).map((section) => {
        const content = section?.content || {};
        const ownsHeroActions = String(section?.type || '') === 'hero'
          && content.hideReaderActionStrip === undefined
          && content.showReaderActionStrip !== true;
        return {
          ...section,
          content: {
            ...content,
            educationTemplateStyle: EDUCATION_TEMPLATE_STYLES.EXAM,
            ...(ownsHeroActions ? { hideReaderActionStrip: true } : {})
          }
        };
      })
    }));
  }

  return resolveTemplateSectionsForReader(base, finalDomain);
};

export const getStoredActiveTemplate = () => {
  try {
    const sessionTemplate = sessionStorage.getItem(ACTIVE_TEMPLATE_SESSION_KEY);
    if (sessionTemplate) return extractTemplatePayload(sessionTemplate);

    const legacyTemplate = localStorage.getItem(ACTIVE_TEMPLATE_STORAGE_KEY);
    if (legacyTemplate) {
      const template = extractTemplatePayload(legacyTemplate);
      if (template) {
        sessionStorage.setItem(ACTIVE_TEMPLATE_SESSION_KEY, JSON.stringify(template));
      }
      return template;
    }

    return null;
  } catch (error) {
    return null;
  }
};

const templateCompanyIdOf = (template) => (
  template?.company?.primaryId
  || template?.companyId
  || template?.attributes?.companyId
);

const templateDomainTypeOf = (template) => (
  template?.domain?.type
  || template?.attributes?.sourceDomain
  || ''
);

const templatePrimaryIdOf = (template) => {
  const id = template?.primaryId ?? template?.publishedVersionId ?? template?.id ?? template?._id;
  const numeric = Number(id);
  return Number.isFinite(numeric) ? numeric : 0;
};

const templateVersionOf = (template) => {
  const version = Number(template?.version ?? template?.attributes?.version ?? 0);
  return Number.isFinite(version) ? version : 0;
};

const templateDateValueOf = (template) => {
  const value = template?.modifiedDate
    || template?.updatedDate
    || template?.updatedAt
    || template?.modifiedOn
    || template?.createdDate
    || template?.createdAt;
  if (!value) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'object' && Number.isFinite(Number(value.time))) return Number(value.time);
  // MongoDB extended JSON: { $date: { $numberLong: "..." } } or { $numberLong: "..." }
  if (typeof value === 'object' && Number.isFinite(Number(value.$numberLong))) return Number(value.$numberLong);
  if (typeof value === 'object' && typeof value.$date === 'object' && Number.isFinite(Number(value.$date?.$numberLong))) return Number(value.$date.$numberLong);
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const templateFreshnessScoreOf = (template) => (
  templateVersionOf(template) * 1_000_000_000_000
  + templateDateValueOf(template)
  + templatePrimaryIdOf(template)
);

const templateScopeRankOf = (template) => {
  if (!template) return 0;
  if (templateCompanyIdOf(template)) return 2;
  if (isSystemDefaultTemplate(template)) return 1;
  return 1;
};

const shouldKeepStoredTemplate = (currentTemplate, incomingTemplate) => {
  if (!currentTemplate || !incomingTemplate) return false;
  const currentDomain = normalizeTemplateDomain(templateDomainTypeOf(currentTemplate));
  const incomingDomain = normalizeTemplateDomain(templateDomainTypeOf(incomingTemplate));
  if (currentDomain && incomingDomain && currentDomain !== incomingDomain) return false;

  const currentRank = templateScopeRankOf(currentTemplate);
  const incomingRank = templateScopeRankOf(incomingTemplate);
  if (currentRank > incomingRank) return true;
  if (currentRank < incomingRank) return false;

  const currentCompanyId = templateCompanyIdOf(currentTemplate);
  const incomingCompanyId = templateCompanyIdOf(incomingTemplate);
  if (currentCompanyId && incomingCompanyId && String(currentCompanyId) !== String(incomingCompanyId)) {
    return false;
  }

  return templateFreshnessScoreOf(currentTemplate) > templateFreshnessScoreOf(incomingTemplate);
};

const isSystemDefaultTemplate = (template) => (
  template?.systemDefault === true
  || String(template?.systemDefault).toLowerCase() === 'true'
);

export const isPublishedActiveTemplateFor = (template, { companyId, domainType } = {}) => {
  const resolvedTemplate = extractTemplatePayload(template);
  if (!resolvedTemplate) return false;

  const templateCompanyId = templateCompanyIdOf(resolvedTemplate);
  const templateDomainType = templateDomainTypeOf(resolvedTemplate);
  const companyMatches = !companyId || !templateCompanyId || String(templateCompanyId) === String(companyId);
  const domainMatches = !domainType
    || !templateDomainType
    || normalizeTemplateDomain(templateDomainType) === normalizeTemplateDomain(domainType);
  const lifecycleStatus = String(resolvedTemplate.lifecycleStatus || 'PUBLISHED').toUpperCase();

  return companyMatches
    && domainMatches
    && !isSystemDefaultTemplate(resolvedTemplate)
    && lifecycleStatus === 'PUBLISHED';
};

export const getPublishedActiveTemplate = (template, context = {}) => {
  const resolvedTemplate = extractTemplatePayload(template);
  return isPublishedActiveTemplateFor(resolvedTemplate, context) ? resolvedTemplate : null;
};

export const getStoredPublishedActiveTemplate = (context = {}, options = {}) => {
  const requireFreshLoad = options.requireFreshLoad !== false;
  if (requireFreshLoad && !hasFreshActiveTemplateApiLoad(context.companyId, context.domainType)) {
    return null;
  }
  return getPublishedActiveTemplate(getStoredActiveTemplate(), context);
};

export const isStoredActiveTemplateFor = (companyId, domainType) => {
  return Boolean(getStoredPublishedActiveTemplate({ companyId, domainType }));
};

/**
 * Persist the given template as the active template in sessionStorage and
 * localStorage, then dispatch `avps-template-updated`.
 *
 * @param {*}       template   - The template to store (or null/undefined to clear).
 * @param {object}  [opts]
 * @param {boolean} [opts.forceStore=false] - When true, skip the
 *   `shouldKeepStoredTemplate` freshness guard and always write the incoming
 *   template. Use this after a definitive API refresh so a freshly published
 *   template always replaces whatever was previously cached.
 */
export const setStoredActiveTemplate = (template, opts = {}) => {
  const resolvedTemplate = extractTemplatePayload(template);
  if (!resolvedTemplate) {
    sessionStorage.removeItem(ACTIVE_TEMPLATE_SESSION_KEY);
    localStorage.removeItem(ACTIVE_TEMPLATE_STORAGE_KEY);
    window.dispatchEvent(new Event('avps-template-updated'));
    return;
  }
  if (!opts?.forceStore) {
    const currentTemplate = getStoredActiveTemplate();
    if (shouldKeepStoredTemplate(currentTemplate, resolvedTemplate)) {
      return;
    }
  }
  sessionStorage.setItem(ACTIVE_TEMPLATE_SESSION_KEY, JSON.stringify(resolvedTemplate));
  localStorage.setItem(ACTIVE_TEMPLATE_STORAGE_KEY, JSON.stringify(resolvedTemplate));
  window.dispatchEvent(new Event('avps-template-updated'));
};

export const findTemplatePage = (template, pageKey, route) => {
  const pages = template?.pages || [];
  const enabledPages = pages.filter((page) => page?.enabled !== false);
  // Defaults are merged before authored pages during reader normalization. Search
  // newest-to-oldest so an authored page wins when it intentionally owns the same
  // route as a default page (for example Premium `careers` vs default `career`).
  const preferredPages = [...enabledPages].reverse();
  const domainType = domainOf(template);
  const currentRoute = normalizeRoute(route);

  // The browser URL is the strongest signal. Shared domain routes still pass legacy
  // page keys such as `career` and `contact`, so checking the key first can select a
  // default page even when the active template publishes `/careers` or `/contactus`.
  return preferredPages.find((page) => currentRoute && routeMatches(currentRoute, page.route))
    || preferredPages.find((page) => pageKeyMatches(page.pageKey, pageKey))
    || preferredPages.find((page) => (
      currentRoute
      && pageAliasesFor(domainType, page.pageKey).some((alias) => routeMatches(currentRoute, alias))
    ))
    || null;
};
